import React, { useState, useEffect, useRef } from 'react';
import { Mic, Square, RefreshCw, Play, Pause, Download, Activity, AlertCircle, Languages } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { getUITranslation } from '../utils/translation';

interface SpeechRecorderProps {
  targetPrompt: string;
  translation?: string;
  isRTL?: boolean;
  targetLanguage?: string;
  onRecordingComplete: (transcript: string) => void;
}

export const SpeechRecorder: React.FC<SpeechRecorderProps> = ({ targetPrompt, translation, isRTL = false, targetLanguage = 'Spanish', onRecordingComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recognitionError, setRecognitionError] = useState<string | null>(null);
  const [showTranslation, setShowTranslation] = useState(true);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    let interval: number;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      try {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onresult = (event: any) => {
          try {
            if (!event || !event.results) return;
            const currentTranscript = Array.from(event.results)
              .map((result: any) => (result[0] ? result[0].transcript : ''))
              .join('');
            setTranscript(currentTranscript);
          } catch (err) {
            console.error("Error processing recognition result:", err);
          }
        };

        recognition.onerror = (event: any) => {
          console.error('Speech recognition error', event.error);
          setRecognitionError(event.error);
        };

        recognitionRef.current = recognition;
      } catch (err) {
        console.error("Error initializing SpeechRecognition:", err);
      }
    }
  }, []);

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Microphone access is not supported in this browser.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (typeof MediaRecorder === 'undefined') {
        alert('MediaRecorder is not supported in this browser.');
        return;
      }

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        
        // Auto-play
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.src = url;
            audioRef.current.play().catch(e => console.error("Auto-play failed:", e));
            setIsPlaying(true);
          }
        }, 100);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      setTranscript('');
      setAudioUrl(null);
      setRecognitionError(null);

      if (recognitionRef.current) {
        try {
          recognitionRef.current.start();
        } catch (e) {
          console.error("Failed to start recognition:", e);
        }
      }
    } catch (err) {
      console.error('Error accessing microphone:', err);
      alert('Error accessing microphone. Please ensure you have granted permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.error("Failed to stop recognition:", e);
        }
      }

      if (transcript) {
        onRecordingComplete(transcript);
      } else {
        // Fallback if transcript is empty (e.g. no speech detected)
        onRecordingComplete("");
      }
    }
  };

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-blue-900/5 border border-slate-100 overflow-hidden h-full flex flex-col">
      <div className="px-5 py-3 border-b border-slate-50 bg-gradient-to-r from-blue-50/50 to-white">
        <h3 className="font-bold text-slate-900 text-base font-display">{getUITranslation('practiceArea', targetLanguage)}</h3>
        <p className="text-[10px] text-blue-500 font-medium uppercase tracking-wider">{getUITranslation('recordAndListen', targetLanguage)}</p>
      </div>

      <div className="p-6 flex flex-col items-center justify-center space-y-6 flex-1">
        {/* Target Prompt Display */}
        <div className="w-full text-center space-y-2">
          <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{getUITranslation('readThisAloud', targetLanguage)}</p>
          <p className="text-xl md:text-2xl font-display font-bold text-slate-800 leading-tight">
            "{targetPrompt}"
          </p>
          
          {translation && (
            <div className="pt-2 flex flex-col items-center">
              <button 
                onClick={() => setShowTranslation(!showTranslation)}
                className="text-xs font-medium text-blue-500 hover:text-blue-600 transition-colors flex items-center gap-1.5 px-3 py-1 rounded-full hover:bg-blue-50"
              >
                <Languages className="w-3 h-3" />
                {showTranslation ? getUITranslation('hideTranslation', targetLanguage) : getUITranslation('showTranslation', targetLanguage)}
              </button>
              
              <AnimatePresence>
                {showTranslation && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <p 
                      className={`text-slate-600 text-base font-medium mt-2 bg-slate-50 px-4 py-2 rounded-lg border border-slate-100 ${isRTL ? 'text-right' : 'text-left'}`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    >
                      {translation}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Recording Controls */}
        <div className="relative">
          {isRecording && (
            <div className="absolute inset-0 rounded-full animate-ping bg-rose-100 opacity-75 scale-150"></div>
          )}
          <button
            onClick={isRecording ? stopRecording : startRecording}
            title={getUITranslation('speak', targetLanguage)}
            className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${
              isRecording
                ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-rose-500/30'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-600/30 hover:scale-105'
            }`}
          >
            {isRecording ? (
              <Square className="w-6 h-6 fill-current" />
            ) : (
              <Mic className="w-8 h-8" />
            )}
          </button>
        </div>

        {/* Status & Timer */}
        <div className="text-center space-y-1">
          {isRecording ? (
            <div className="flex items-center gap-2 text-rose-500 text-sm font-medium animate-pulse">
              <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
              Recording... {formatTime(recordingTime)}
            </div>
          ) : (
            <p className="text-slate-400 text-sm font-medium">{getUITranslation('tapMicrophoneToStart', targetLanguage)}</p>
          )}
          
          {recognitionError && (
            <div className="flex items-center gap-2 text-amber-600 text-xs bg-amber-50 px-2 py-0.5 rounded-full">
              <AlertCircle className="w-3 h-3" />
              <span>Microphone access needed</span>
            </div>
          )}
        </div>

        {/* Playback Controls */}
        {audioUrl && !isRecording && (
          <div className="w-full max-w-sm bg-slate-50 rounded-xl p-3 flex items-center justify-between gap-3 animate-fade-in border border-slate-100">
            <button
              onClick={togglePlayback}
              className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-blue-600 hover:bg-blue-50 transition-colors shadow-sm"
            >
              {isPlaying ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
            </button>
            
            <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden">
              <div className={`h-full bg-blue-500 transition-all duration-300 ${isPlaying ? 'w-full animate-[width_2s_linear]' : 'w-0'}`}></div>
            </div>

            <div className="flex gap-1">
              <button 
                onClick={() => {
                  setAudioUrl(null);
                  setTranscript(null);
                }}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                title="Record Again"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <a
                href={audioUrl}
                download="practice-recording.webm"
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </a>
            </div>
            
            <audio
              ref={audioRef}
              src={audioUrl}
              onEnded={handleAudioEnded}
              className="hidden"
            />
          </div>
        )}
      </div>
    </div>
  );
};
